import { ChevronRightIcon, HomeIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/common/state/hooks";
import { Link, useLocation } from "react-router-dom";
import type { RootState } from "src/common/state/store";
import { getSelectedProjectGroup } from "../../../features/projectGroups/state/projectGroupsSlice";
import { getSelectedProjectId } from "../../../features/stats/state/statsSlice";
import { NavigationItem } from "./NavigationItem";

interface NavigationProps {
  titles: string | React.JSX.Element;
}

const Breadcrumb = ({ titles }: NavigationProps) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const [pathnames, setPathnames] = useState<string[]>([]);
  const [pathRoute, setPathRoute] = useState<string[]>([]);

  // using the selected project group and project id to display the breadcrumb
  const selectedProjectGroup = useAppSelector(getSelectedProjectGroup);
  const selectedProjectId = useAppSelector(getSelectedProjectId);

  // Getting deleted project group from the "stats slice"
  const projectGroups = useAppSelector((state: RootState) => state.stats.allSessionsByProjectGroup);
  const selectedProjectGroupId = useAppSelector(
    (state: RootState) => state.stats.selectedProjectGroupId,
  );

  const getDeletedProjectGroupName = () => {
    const projectGroup = projectGroups.find(
      (group) => group.project_group_id === selectedProjectGroupId,
    );

    return projectGroup ? projectGroup.project_group_name : "courses";
  };

  //For calculate the length paths
  const pathArray = pathname.split("/").filter(Boolean);
  const lengthPath = pathArray.length;

  //const for not display the Project groups in double

  useEffect(() => {
    if (pathname.startsWith("/profile")) {
      setPathnames(["profile", pathArray[0]]);
      setPathRoute(["profile", pathArray[0]]);
    } else if (pathArray[0] === "templates") {
      setPathRoute(["templates"]);
      setPathnames(["templates"]);
    } else if (pathArray[0] === "courses") {
      const pathArrayWithProjectGroupId = pathArray.slice(0, 2);
      setPathnames(pathArrayWithProjectGroupId);
      setPathRoute(pathArrayWithProjectGroupId);
    } else if (pathArray[0] === "medias") {
      setPathnames(["medias"]);
      setPathRoute(["medias"]);
    } else if (pathArray[0] === "statistics") {
      const newArrayPaths = pathArray.slice(0, 3);
      newArrayPaths[1] = getDeletedProjectGroupName();

      if (selectedProjectId) {
        newArrayPaths.push(`${selectedProjectId}`); // add the selectedProjectId to the breadcrumb
      }
      setPathRoute(pathArray.slice(0, 3));
      setPathnames(newArrayPaths);
    } else if (pathArray[0] === "chat") {
      if (lengthPath >= 2) {
        if (pathArray[1] === "scenario-helper") {
          setPathRoute(["chat", "scenario-helper"]);
          setPathnames(["chat", "scenario-helper"]);
        }

        if (pathArray[1] === "wixarpedia") {
          setPathRoute(["chat", "wixarpedia"]);
          setPathnames(["chat", "wixarpedia"]);
        }
      }
    } else {
      setPathnames(pathArray);
    }
  }, [selectedProjectGroup, selectedProjectId, pathname]);

  let profileLabel = "";
  if (pathname === "/profile/me") {
    profileLabel = t("pages.profile.tabs.myDetails");
  } else if (pathname === "/profile/organization") {
    profileLabel = t("pages.profile.tabs.myOrganization");
  } else if (pathname === "/profile/subscription") {
    profileLabel = t("pages.profile.tabs.subscription");
  }

  return (
    <nav className="flex mb-3" aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 md:space-x-2">
        {pathnames.length ? (
          <li className="inline-flex items-center">
            <Link
              to={"/"}
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              <HomeIcon className="mr-2 w-4 h-4" />
              {t("general.home")}
            </Link>
          </li>
        ) : (
          <HomeIcon className="mr-2 w-4 h-4 inline-flex items-center text-sm font-medium text-gray-700" />
        )}

        {pathname.startsWith("/profile") && (
          <li className="inline-flex items-center">
            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
            <Link to="/profile/me" className="ml-1 text-sm font-medium text-gray-400 md:ml-2">
              {t("pages.profile.myProfile")}
            </Link>
          </li>
        )}

        {profileLabel && (
          <li className="inline-flex items-center">
            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
            <span className="ml-1 text-sm font-medium text-gray-700">{profileLabel}</span>
          </li>
        )}

        {pathnames
          .filter((p) => p && !p.startsWith("profile"))
          .map((name, index) => {
            return (
              <NavigationItem
                key={`${index}_`}
                name={name}
                index={index}
                titles={titles}
                pathRoute={pathRoute}
                pathnames={pathnames}
                pathArray={pathArray}
                pathname={pathname}
                selectedProjectGroup={selectedProjectGroup}
              />
            );
          })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
